/**
 * Photo List Settings page (/photos/daily/settings) — the operator-editable
 * half of the Daily Photo List's rules, per dealer group: the unified USED
 * recon-step table (color / priority / excluded), the lot vocabulary +
 * Stargard address aliases, the per-rooftop lot priorities, and the color
 * rules. What used to need a code deploy (a new Stargard address, a renamed
 * recon step) is an edit here.
 *
 * One section at a time, picked from a menu (Ian, 2026-09-28: the page had
 * grown too long to scroll back up through). The menu groups the sections as
 * Definitions (Address Aliases, Recon Steps, Lots) and Rules (NEW Walking
 * Order, USED Front-Lot Boosts, Color Rules), chosen from mockup A of three.
 * The open section is kept in the address (?section=lots), so a refresh or a
 * shared link lands on it. Every section stays mounted and the others are
 * hidden, so a draft or a lot's refusal message survives a trip to another
 * section.
 *
 * Naming and placement, recorded per the PerformanceReportPage precedent:
 * named "Photo List Settings" — not "Preferences" (/photo-list/preferences
 * already means per-user sheet selection) and not "Configuration" (Photos →
 * Configuration is photo-capture config). Reached from the gear button in
 * the Daily Photo List header, no sidebar entry — a sibling secondary page
 * to /photos/daily/new-run, whose skeleton this copies
 * (PhotoListNewRunPage.tsx ~lines 113-141: back-link, h1, content keyed by
 * dealer group). Deliberate divergence: no PhotoListProvider — this page
 * needs no runs/vehicles and must not start the 30s file-status poll; it
 * loads its own data.
 *
 * Unseeded groups see the built-in defaults read-only with one "Customize
 * for this group" button (the Photo Configuration "Seed Canonical" empty-
 * state precedent): editing starts from a copy of the proven defaults, and
 * the fallback rule ("no rows = built-ins") stays visible instead of
 * surprising. Save model is edit-in-place autosave throughout — no page-level
 * Save button, matching Photo Configuration.
 */
import React, { useCallback, useEffect, useRef, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"

import Layout from "../../components/Layout"
import DealerGroupRequiredNotice from "../../components/DealerGroupRequiredNotice"
import HighlightRulesPanel from "../../components/photos/HighlightRulesPanel"
import LotPrioritiesPanel from "../../components/photos/LotPrioritiesPanel"
import PhotoListSettingsMenu, { SettingsMenuGroup } from "../../components/photos/PhotoListSettingsMenu"
import ReconStepsPanel from "../../components/photos/ReconStepsPanel"
import StargardAliasesByFile from "../../components/photos/StargardAliasesByFile"
import StargardMappingPanel from "../../components/photos/StargardMappingPanel"
import UnrecognizedPanel from "../../components/photos/UnrecognizedPanel"
import { useAuth } from "../../context/AuthContext"
import { useDealerGroup } from "../../context/DealerGroupContext"
import {
  PhotoListLot,
  PhotoListReconStep,
  PhotoListSettings,
  fetchPhotoListSettings,
  seedPhotoListSettings,
} from "../../api/photoList"
import { THEME } from "../../config/theme"
import { useIsMobile } from "../../hooks/useIsMobile"

const SECTIONS = ["aliases", "recon", "lots", "walking", "boosts", "colors"] as const
type SettingsSection = (typeof SECTIONS)[number]

const SECTION_LABELS: Record<SettingsSection, string> = {
  aliases: "Address Aliases",
  recon: "Recon Steps",
  lots: "Lots",
  walking: "NEW Walking Order",
  boosts: "USED Front-Lot Boosts",
  colors: "Color Rules",
}

const SECTION_GROUPS: Array<{ label: string; sections: SettingsSection[] }> = [
  { label: "Definitions", sections: ["aliases", "recon", "lots"] },
  { label: "Rules", sections: ["walking", "boosts", "colors"] },
]

const DEFAULT_SECTION: SettingsSection = "aliases"
const SECTION_PARAM = "section"

function isSection(value: string | null): value is SettingsSection {
  return value !== null && (SECTIONS as readonly string[]).includes(value)
}

export default function PhotoListSettingsPage() {
  return (
    <Layout>
      <PhotoListSettingsPageBody />
    </Layout>
  )
}

function PhotoListSettingsPageBody() {
  const { dealerGroupId, activeGroupName } = useDealerGroup()
  const { hasPermission, isLoading: authLoading } = useAuth()
  const isMobile = useIsMobile()
  const navigate = useNavigate()

  // Double gate (ScheduledReportsPage idiom): the route already requires the
  // permission, but a hand-typed URL must bounce without flashing content.
  useEffect(() => {
    if (!authLoading && !hasPermission("manage_photo_list_settings")) {
      navigate("/dashboard", { replace: true })
    }
  }, [authLoading, hasPermission, navigate])
  if (authLoading || !hasPermission("manage_photo_list_settings")) {
    return <div />
  }

  if (dealerGroupId === null) {
    return (
      <div style={{ padding: isMobile ? THEME.spacing.md : THEME.spacing.lg }}>
        <DealerGroupRequiredNotice surface="photo list settings" />
      </div>
    )
  }

  return (
    <PhotoListSettingsContent
      key={dealerGroupId}
      dealerGroupId={dealerGroupId}
      groupName={activeGroupName ?? "this group"}
      isMobile={isMobile}
    />
  )
}

function PhotoListSettingsContent({
  dealerGroupId,
  groupName,
  isMobile,
}: {
  dealerGroupId: number
  groupName: string
  isMobile: boolean
}) {
  const [settings, setSettings] = useState<PhotoListSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [seeding, setSeeding] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  // Every server answer merged in place (a row patch, a rules write) bumps
  // `localWrites`. A reload that started before one returned a snapshot from
  // before it, and applying that undid the change on screen; an older reload
  // landing after a newer one did the same (review, 2026-09-25).
  const localWrites = useRef(0)
  const latestLoad = useRef(0)

  // `keepMessage`: a reload that must not clear the message on screen (a
  // lot rename's "check the walking orders and boosts" warning).
  const load = useCallback(
    async (options: { keepMessage?: boolean } = {}) => {
      const loadId = ++latestLoad.current
      try {
        for (let attempt = 0; attempt < 3; attempt++) {
          const writesAtStart = localWrites.current
          const loaded = await fetchPhotoListSettings(dealerGroupId, SETTINGS_OPTIONS)
          if (loadId !== latestLoad.current) return
          // A change was saved while this loaded: load again.
          if (writesAtStart !== localWrites.current && attempt < 2) continue
          setSettings(loaded)
          if (!options.keepMessage) setError(null)
          return
        }
      } catch (err) {
        if (loadId === latestLoad.current) {
          setError(err instanceof Error ? err.message : "Failed to load settings")
        }
      } finally {
        if (loadId === latestLoad.current) setLoading(false)
      }
    },
    [dealerGroupId],
  )

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const loaded = await fetchPhotoListSettings(dealerGroupId, SETTINGS_OPTIONS)
        if (!cancelled) {
          setSettings(loaded)
          setError(null)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load settings")
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dealerGroupId])

  const handleSeed = async () => {
    setSeeding(true)
    setError(null)
    try {
      await seedPhotoListSettings(dealerGroupId)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to customize settings")
    } finally {
      setSeeding(false)
    }
  }

  // Edit-in-place: replace one row with the server's returned copy.
  const patchRow = <K extends keyof PhotoListSettings>(
    listKey: K,
    row: { id: number | null },
  ) => {
    localWrites.current += 1
    setSettings((prev) => {
      if (!prev) return prev
      const list = prev[listKey] as Array<{ id: number | null }>
      return {
        ...prev,
        [listKey]: list.map((r) => (r.id === row.id ? row : r)),
      }
    })
  }

  // A server deployed before color rules sends none of them: no section and
  // no menu entry, rather than a crashed page (review, 2026-09-25).
  const hasColorRules = Boolean(settings?.highlight_options)
  const isAvailable = (id: SettingsSection) => id !== "colors" || hasColorRules
  const requested = searchParams.get(SECTION_PARAM)
  const section: SettingsSection =
    isSection(requested) && isAvailable(requested) ? requested : DEFAULT_SECTION

  const selectSection = (id: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set(SECTION_PARAM, id)
        return next
      },
      { replace: true },
    )
    // Every section opens at the top of the page: staying scrolled opened the
    // next one partway down (Ian, 2026-09-28). It is the window that scrolls
    // here — Layout's <main> grows with its content rather than scrolling.
    window.scrollTo({ top: 0 })
  }

  // Recon-board steps no rule knows yet: they wait inside Recon Steps, so
  // the menu says how many.
  const unrecognizedSteps = settings?.unrecognized.recon_steps.length ?? 0
  const menuGroups: SettingsMenuGroup[] = SECTION_GROUPS.map((group) => ({
    label: group.label,
    items: group.sections.filter(isAvailable).map((id) => ({
      id,
      label: SECTION_LABELS[id],
      badge: id === "recon" && unrecognizedSteps > 0 ? `${unrecognizedSteps} new` : undefined,
    })),
  }))

  const anyUnseeded =
    settings !== null &&
    (settings.stargard_source !== "database" ||
      settings.recon_source !== "database" ||
      settings.lot_priority_source !== "database")

  return (
    <div
      style={{
        padding: isMobile ? THEME.spacing.md : THEME.spacing.lg,
        maxWidth: PAGE_MAX_WIDTH,
        margin: "0 auto",
      }}
    >
      <Link
        to="/photos/daily"
        style={{
          fontSize: 12.5,
          color: THEME.colors.neutral.light.textSecondary,
          textDecoration: "none",
        }}
      >
        ← Daily Photo List
      </Link>
      <h1
        style={{
          fontSize: isMobile ? 20 : 24,
          fontWeight: 500,
          letterSpacing: "-0.5px",
          margin: `4px 0 ${THEME.spacing.md}`,
          color: THEME.colors.neutral.light.textPrimary,
        }}
      >
        Photo List Settings
      </h1>

      {error && <div style={errorBannerStyle}>{error}</div>}

      {loading && <div style={loadingTextStyle}>Loading settings…</div>}

      {!loading && settings && (
        <>
          {anyUnseeded && (
            <div style={customizeBannerStyle}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontWeight: 500, marginBottom: 2 }}>
                  Showing INVI's built-in defaults
                </div>
                <div style={{ fontSize: 13, color: THEME.colors.neutral.light.textSecondary }}>
                  Customize to copy them for {groupName} and start editing.
                  Until then the photo list keeps using the built-ins.
                </div>
              </div>
              <button
                type="button"
                onClick={handleSeed}
                disabled={seeding}
                style={customizeButtonStyle(seeding)}
              >
                {seeding ? "Copying…" : "Customize for this group"}
              </button>
            </div>
          )}

          <div style={bodyStyle(isMobile)}>
            <PhotoListSettingsMenu
              groups={menuGroups}
              selected={section}
              onSelect={selectSection}
              isMobile={isMobile}
            />

            <div style={sectionColumnStyle}>
              {/* What each store's Stargard file said, and the rule that
                  covers each text (Ian, 2026-09-23). */}
              <section aria-label={SECTION_LABELS.aliases} hidden={section !== "aliases"}>
                <StargardAliasesByFile
                  lots={settings.lots}
                  aliases={settings.stargard_aliases}
                  // "?? null": a server deployed before this field existed omits it,
                  // and the section then falls back to listing every rule.
                  stargardSeen={settings.stargard_seen ?? null}
                  stargardFileOptions={settings.stargard_file_options}
                  editable={settings.stargard_source === "database"}
                  isMobile={isMobile}
                  dealerGroupId={dealerGroupId}
                  onStructureChanged={load}
                  onError={setError}
                />
              </section>

              <section aria-label={SECTION_LABELS.recon} hidden={section !== "recon"}>
                <UnrecognizedPanel
                  unrecognized={settings.unrecognized}
                  reconEditable={settings.recon_source === "database"}
                  dealerGroupId={dealerGroupId}
                  onRuleAdded={load}
                  onError={setError}
                />
                <ReconStepsPanel
                  steps={settings.recon_steps}
                  editable={settings.recon_source === "database"}
                  dealerGroupId={dealerGroupId}
                  onRowSaved={(row: PhotoListReconStep) => patchRow("recon_steps", row)}
                  onStructureChanged={load}
                  onError={setError}
                />
              </section>

              <section aria-label={SECTION_LABELS.lots} hidden={section !== "lots"}>
                <StargardMappingPanel
                  lots={settings.lots}
                  aliases={settings.stargard_aliases}
                  newLotPriorities={settings.new_lot_priorities}
                  usedLotBoosts={settings.used_lot_boosts}
                  editable={settings.stargard_source === "database"}
                  dealerGroupId={dealerGroupId}
                  onLotSaved={(row: PhotoListLot) => {
                    patchRow("lots", row)
                    // Address Aliases rows quote lot names from their own snapshot:
                    // reload so a rename shows everywhere, not just in Lots. Keep
                    // the message: the rename's warning arrives right after this,
                    // and the reload used to clear it (review, 2026-09-25).
                    void load({ keepMessage: true })
                  }}
                  onStructureChanged={load}
                  onError={setError}
                />
              </section>

              <section aria-label={SECTION_LABELS.walking} hidden={section !== "walking"}>
                <LotPrioritiesPanel
                  kind="new"
                  newPriorities={settings.new_lot_priorities}
                  usedBoosts={settings.used_lot_boosts}
                  lots={settings.lots}
                  rooftopOptions={settings.rooftop_options}
                  editable={settings.lot_priority_source === "database"}
                  dealerGroupId={dealerGroupId}
                  onStructureChanged={load}
                  onError={setError}
                />
              </section>

              <section aria-label={SECTION_LABELS.boosts} hidden={section !== "boosts"}>
                <LotPrioritiesPanel
                  kind="used"
                  newPriorities={settings.new_lot_priorities}
                  usedBoosts={settings.used_lot_boosts}
                  lots={settings.lots}
                  rooftopOptions={settings.rooftop_options}
                  editable={settings.lot_priority_source === "database"}
                  dealerGroupId={dealerGroupId}
                  onStructureChanged={load}
                  onError={setError}
                />
              </section>

              {settings.highlight_options && (
                <section aria-label={SECTION_LABELS.colors} hidden={section !== "colors"}>
                  <HighlightRulesPanel
                    rules={settings.highlight_rules ?? []}
                    source={settings.highlight_source ?? "code_defaults"}
                    options={settings.highlight_options}
                    rooftopOptions={settings.rooftop_options}
                    dealerGroupId={dealerGroupId}
                    onRulesChanged={(payload) => {
                      localWrites.current += 1
                      setSettings((prev) => (prev ? { ...prev, ...payload } : prev))
                    }}
                    onError={setError}
                  />
                </section>
              )}

              <p style={footnoteStyle}>
                Changes apply from the next generated list (and to re-exports of
                the current one). Screen colors refresh the next time the Daily
                Photo List loads. The built-in defaults come back only when a whole
                set of sections is empty: Recon Steps on its own, Lots with Address
                Aliases, and NEW Walking Order with USED Front-Lot Boosts. Emptying
                just one section of a pair leaves it empty. Customize restores the
                defaults.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// --- Local styles (banner from photoConfigShared errorBannerStyle idiom) ---

// The sections kept their old 1160px page; the menu's column is added on.
const PAGE_MAX_WIDTH = 1160 + 240 + 24

const errorBannerStyle: React.CSSProperties = {
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  marginBottom: THEME.spacing.md,
  fontSize: 13,
  color: THEME.colors.semantic.danger,
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.semantic.danger}`,
  borderRadius: THEME.borderRadius.md,
}

/** This page lays out Address Aliases from the latest run. */
const SETTINGS_OPTIONS = { includeStargardSeen: true }

const loadingTextStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
}

const customizeBannerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: THEME.spacing.md,
  padding: THEME.spacing.md,
  marginBottom: THEME.spacing.lg,
  fontSize: 14,
  color: THEME.colors.neutral.light.textPrimary,
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
}

const customizeButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  background: THEME.colors.navy,
  color: THEME.colors.white,
  border: "none",
  borderRadius: THEME.borderRadius.md,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  fontFamily: THEME.fonts.primary,
  whiteSpace: "nowrap",
})

// Menu beside the section; on a phone the menu's picker sits above it.
const bodyStyle = (isMobile: boolean): React.CSSProperties => ({
  display: "flex",
  flexDirection: isMobile ? "column" : "row",
  alignItems: isMobile ? "stretch" : "flex-start",
  gap: isMobile ? THEME.spacing.md : THEME.spacing.lg,
})

const sectionColumnStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
}

const footnoteStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: THEME.colors.neutral.light.textSecondary,
  lineHeight: 1.5,
  marginBottom: THEME.spacing.xl,
}
